export const AGENT_ACTIVITY_STAGES = [
  "understanding",
  "planning",
  "reasoning",
  "searching_web",
  "crawling",
  "searching_knowledge",
  "reading_sources",
  "analyzing",
  "using_tool",
  "generating_image",
  "generating_document",
  "composing",
  "validating",
  "success",
  "retrying",
  "error",
  "cancelled",
] as const;

export type AgentActivityStage = (typeof AGENT_ACTIVITY_STAGES)[number];

export const AGENT_ACTIVITY_EVENT_STATUSES = [
  "queued",
  "active",
  "completed",
  "failed",
  "cancelled",
] as const;

export type AgentActivityEventStatus = (typeof AGENT_ACTIVITY_EVENT_STATUSES)[number];
export type AgentActivityRunStatus = "running" | "completed" | "error" | "cancelled";
export type AgentActivityItemKind = "sources" | "documents" | "queries" | "concepts";

export const AGENT_ACTIVITY_MESSAGES = {
  "understanding.focus": "Getting your request into focus…",
  "understanding.context": "Confirming the request and available context…",
  "planning.route": "Mapping the best approach…",
  "planning.steps": "Organizing the next useful steps…",
  "reasoning.signals": "Connecting the important signals…",
  "reasoning.options": "Comparing the available options…",
  "reasoning.evidence": "Evaluating the available findings…",
  "reasoning.request": "Comparing the request context…",
  "searching_web.scan": "Scanning trusted web sources…",
  "searching_web.check": "Checking the requested public sources…",
  "crawling.pages": "Exploring relevant company pages…",
  "crawling.links": "Following the relevant page links…",
  "searching_knowledge.perlab": "Checking the PERLab knowledge base…",
  "searching_knowledge.matches": "Finding the strongest knowledge matches…",
  "reading_sources.review": "Reviewing the most relevant sources…",
  "reading_sources.highlights": "Identifying the useful source passages…",
  "analyzing.patterns": "Comparing evidence and identifying patterns…",
  "analyzing.signals": "Finding the strongest signals…",
  "using_tool.run": "Running the required tool…",
  "using_tool.result": "Checking the tool result…",
  "generating_image.shape": "Shaping the first visual…",
  "generating_image.details": "Turning the request into something visual…",
  "generating_document.build": "Building the document…",
  "generating_document.structure": "Structuring the document content…",
  "composing.together": "Bringing the findings together…",
  "composing.clear": "Organizing a clear response…",
  "validating.quality": "Running a final quality check…",
  "validating.output": "Checking the generated output…",
  "success.ready": "Ready. Your result is complete.",
  "success.clarification": "Ready. A little more detail is needed.",
  "success.evidence_gap": "Ready. The available evidence was limited.",
  "retrying.recover": "Recovering and trying again…",
  "retrying.reconnect": "Reconnecting to the required service…",
  "error.failed": "The operation could not be completed.",
  "error.temporary": "The service is temporarily unavailable.",
  "error.validation": "The request needs to be revised before trying again.",
  "error.access": "This operation is not available in the current scope.",
  "error.rate_limited": "The service is busy. Try again shortly.",
  "cancelled.stopped": "Generation stopped.",
} as const;

export type AgentActivityMessageKey = keyof typeof AGENT_ACTIVITY_MESSAGES;

export const AGENT_ACTIVITY_COPY: Readonly<
  Record<AgentActivityStage, { title: string; messageKeys: readonly AgentActivityMessageKey[] }>
> = {
  understanding: {
    title: "Understanding your request",
    messageKeys: ["understanding.focus", "understanding.context"],
  },
  planning: {
    title: "Planning the approach",
    messageKeys: ["planning.route", "planning.steps"],
  },
  reasoning: {
    title: "Evaluating the available evidence",
    messageKeys: [
      "reasoning.request",
      "reasoning.signals",
      "reasoning.options",
      "reasoning.evidence",
    ],
  },
  searching_web: {
    title: "Searching trusted web sources",
    messageKeys: ["searching_web.scan", "searching_web.check"],
  },
  crawling: {
    title: "Exploring relevant websites",
    messageKeys: ["crawling.pages", "crawling.links"],
  },
  searching_knowledge: {
    title: "Searching PERLab knowledge",
    messageKeys: ["searching_knowledge.perlab", "searching_knowledge.matches"],
  },
  reading_sources: {
    title: "Reading the selected sources",
    messageKeys: ["reading_sources.review", "reading_sources.highlights"],
  },
  analyzing: {
    title: "Analyzing the evidence",
    messageKeys: ["analyzing.patterns", "analyzing.signals"],
  },
  using_tool: {
    title: "Using a required tool",
    messageKeys: ["using_tool.run", "using_tool.result"],
  },
  generating_image: {
    title: "Creating your visual",
    messageKeys: ["generating_image.shape", "generating_image.details"],
  },
  generating_document: {
    title: "Creating your document",
    messageKeys: ["generating_document.build", "generating_document.structure"],
  },
  composing: {
    title: "Composing the response",
    messageKeys: ["composing.together", "composing.clear"],
  },
  validating: {
    title: "Validating the result",
    messageKeys: ["validating.quality", "validating.output"],
  },
  success: {
    title: "Result ready",
    messageKeys: ["success.ready"],
  },
  retrying: {
    title: "Trying the operation again",
    messageKeys: ["retrying.recover", "retrying.reconnect"],
  },
  error: {
    title: "Operation not completed",
    messageKeys: ["error.failed"],
  },
  cancelled: {
    title: "Operation stopped",
    messageKeys: ["cancelled.stopped"],
  },
};

export const AGENT_ACTIVITY_TOOL_LABELS = {
  public_web_search: "Public web search",
  knowledge_search: "PERLab knowledge search",
  image_generation: "Image generation",
  document_generation: "Document generation",
} as const;

export type AgentActivityToolName = keyof typeof AGENT_ACTIVITY_TOOL_LABELS;

/**
 * Canonical execution event. `message` is accepted for transport compatibility but is
 * deliberately discarded at the browser boundary. Only allowlisted `messageKey` copy is rendered.
 */
export interface AgentActivityEvent {
  schemaVersion: 1;
  runId: string;
  eventId: string;
  sequence: number;
  attempt?: number;
  stage: AgentActivityStage;
  status: AgentActivityEventStatus;
  messageKey?: AgentActivityMessageKey;
  message?: string;
  progress?: number;
  completedItems?: number;
  totalItems?: number;
  itemKind?: AgentActivityItemKind;
  toolName?: string;
  startedAt?: string;
  timestamp: string;
}

export interface AgentActivityDetail {
  id: string;
  label: string;
}

export interface AgentActivityRunState {
  runId: string;
  status: AgentActivityRunStatus;
  currentStage: AgentActivityStage | null;
  startedAt: string;
  endedAt?: string;
  lastSequence: number;
  events: readonly AgentActivityEvent[];
  bufferedEvents: readonly AgentActivityEvent[];
  seenEventIds: readonly string[];
  details: readonly AgentActivityDetail[];
  retryable: boolean;
  /** Set only from the validated final response, never from a transport success event. */
  responseStatus?: AgentActivityCompletionFacts["response_status"];
}

export interface AgentActivityStageItem {
  key: string;
  stage: AgentActivityStage;
  status: AgentActivityEventStatus;
  attempt: number;
  messageKey?: AgentActivityMessageKey;
  progress?: number;
  completedItems?: number;
  totalItems?: number;
  itemKind?: AgentActivityItemKind;
  toolName?: AgentActivityToolName;
}

export interface TruthfulActivityFallbackInput {
  runId: string;
  mode: "chat" | "image";
  researchIntent?: "standard" | "market_benchmark" | "public_web_fallback";
  knowledgeScope?: "current" | "global";
  startedAt?: string;
}

export interface AgentActivityCompletionFacts {
  mode?: "chat" | "image";
  response_status?: "answered" | "clarification" | "insufficient_evidence";
  citations?: readonly unknown[];
  image?: unknown | null;
  grounding?: {
    retrieved_count?: number;
    cited_count?: number;
    bing_query_urls?: readonly string[];
    global_retrieved_count?: number;
  };
}

export type AgentActivityFailureKind =
  | "unknown"
  | "temporary"
  | "validation"
  | "access"
  | "rate_limited";

export interface AgentActivityFailure {
  kind?: AgentActivityFailureKind;
  retryable?: boolean;
  timestamp?: string;
}

const STAGE_SET = new Set<string>(AGENT_ACTIVITY_STAGES);
const EVENT_STATUS_SET = new Set<string>(AGENT_ACTIVITY_EVENT_STATUSES);
const MESSAGE_KEY_SET = new Set<string>(Object.keys(AGENT_ACTIVITY_MESSAGES));
const TOOL_NAME_SET = new Set<string>(Object.keys(AGENT_ACTIVITY_TOOL_LABELS));
const ITEM_KIND_SET = new Set<string>(["sources", "documents", "queries", "concepts"]);
const TERMINAL_STAGE_STATUSES = new Set<AgentActivityEventStatus>([
  "completed",
  "failed",
  "cancelled",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validTimestamp(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && Number.isFinite(Date.parse(value));
}

function optionalNonNegativeInteger(value: unknown): number | undefined {
  return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : undefined;
}

function optionalProgress(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100
    ? value
    : undefined;
}

function normalizedAttempt(value: unknown): number {
  return Number.isInteger(value) && Number(value) > 0 ? Number(value) : 1;
}

function nowTimestamp(timestamp?: string): string {
  return validTimestamp(timestamp) ? timestamp : new Date().toISOString();
}

/** Parse and sanitize an event received across an external boundary. */
export function parseAgentActivityEvent(value: unknown): AgentActivityEvent | null {
  if (!isRecord(value)) return null;
  if (value.schemaVersion !== 1) return null;
  if (typeof value.runId !== "string" || !value.runId.trim()) return null;
  if (typeof value.eventId !== "string" || !value.eventId.trim()) return null;
  if (!Number.isInteger(value.sequence) || Number(value.sequence) <= 0) return null;
  if (typeof value.stage !== "string" || !STAGE_SET.has(value.stage)) return null;
  if (typeof value.status !== "string" || !EVENT_STATUS_SET.has(value.status)) return null;
  if (!validTimestamp(value.timestamp)) return null;

  const totalItems = optionalNonNegativeInteger(value.totalItems);
  const completedItems = optionalNonNegativeInteger(value.completedItems);
  const countsAreConsistent = (
    totalItems === undefined
    || completedItems === undefined
    || completedItems <= totalItems
  );
  const messageKey = typeof value.messageKey === "string" && MESSAGE_KEY_SET.has(value.messageKey)
    ? value.messageKey as AgentActivityMessageKey
    : undefined;
  const itemKind = typeof value.itemKind === "string" && ITEM_KIND_SET.has(value.itemKind)
    ? value.itemKind as AgentActivityItemKind
    : undefined;
  const toolName = typeof value.toolName === "string" && TOOL_NAME_SET.has(value.toolName)
    ? value.toolName
    : undefined;

  return {
    schemaVersion: 1,
    runId: value.runId.trim(),
    eventId: value.eventId.trim(),
    sequence: Number(value.sequence),
    attempt: normalizedAttempt(value.attempt),
    stage: value.stage as AgentActivityStage,
    status: value.status as AgentActivityEventStatus,
    ...(messageKey ? { messageKey } : {}),
    ...(optionalProgress(value.progress) !== undefined
      ? { progress: optionalProgress(value.progress) }
      : {}),
    ...(countsAreConsistent && completedItems !== undefined ? { completedItems } : {}),
    ...(countsAreConsistent && totalItems !== undefined ? { totalItems } : {}),
    ...(itemKind ? { itemKind } : {}),
    ...(toolName ? { toolName } : {}),
    ...(validTimestamp(value.startedAt) ? { startedAt: value.startedAt } : {}),
    timestamp: value.timestamp,
  };
}

export function createAgentActivityRunState(
  runId: string,
  startedAt?: string,
): AgentActivityRunState {
  const normalizedRunId = runId.trim();
  if (!normalizedRunId) throw new Error("Agent activity run id must not be blank.");
  return {
    runId: normalizedRunId,
    status: "running",
    currentStage: null,
    startedAt: nowTimestamp(startedAt),
    lastSequence: 0,
    events: [],
    bufferedEvents: [],
    seenEventIds: [],
    details: [],
    retryable: false,
  };
}

function latestStageAttemptEvent(
  events: readonly AgentActivityEvent[],
  event: AgentActivityEvent,
): AgentActivityEvent | undefined {
  const attempt = event.attempt ?? 1;
  return [...events].reverse().find((candidate) => (
    candidate.stage === event.stage && (candidate.attempt ?? 1) === attempt
  ));
}

function isValidStageTransition(
  events: readonly AgentActivityEvent[],
  event: AgentActivityEvent,
): boolean {
  const previous = latestStageAttemptEvent(events, event);
  if (!previous) return true;
  if (TERMINAL_STAGE_STATUSES.has(previous.status)) return false;
  if (previous.status === "active" && event.status === "queued") return false;
  if (
    previous.progress !== undefined
    && event.progress !== undefined
    && event.progress < previous.progress
  ) return false;
  if (
    previous.completedItems !== undefined
    && event.completedItems !== undefined
    && event.completedItems < previous.completedItems
  ) return false;
  return true;
}

function terminalRunStatus(event: AgentActivityEvent): AgentActivityRunStatus | null {
  if (event.stage === "success" && event.status === "completed") return "completed";
  if (event.stage === "error" && event.status === "failed") return "error";
  if (event.stage === "cancelled" && event.status === "cancelled") return "cancelled";
  return null;
}

function applySequentialEvent(
  state: AgentActivityRunState,
  event: AgentActivityEvent,
): AgentActivityRunState {
  const transitionAllowed = isValidStageTransition(state.events, event);
  const terminalStatus = transitionAllowed ? terminalRunStatus(event) : null;
  const currentStage = transitionAllowed && (
    event.status !== "queued" || state.currentStage === null
  )
    ? event.stage
    : state.currentStage;
  return {
    ...state,
    status: terminalStatus ?? state.status,
    currentStage,
    endedAt: terminalStatus ? event.timestamp : state.endedAt,
    lastSequence: event.sequence,
    events: transitionAllowed ? [...state.events, event] : state.events,
    bufferedEvents: terminalStatus ? [] : state.bufferedEvents,
  };
}

/**
 * Apply one event without mutating state. Events for another run, duplicates, and events after a
 * terminal state are ignored. Sequence gaps are buffered and drained when the missing event arrives.
 */
export function reduceAgentActivityEvent(
  state: AgentActivityRunState,
  incoming: unknown,
): AgentActivityRunState {
  if (state.status !== "running") return state;
  const event = parseAgentActivityEvent(incoming);
  if (!event || event.runId !== state.runId) return state;
  if (state.seenEventIds.includes(event.eventId)) return state;
  if (event.sequence <= state.lastSequence) return state;
  if (state.bufferedEvents.some((candidate) => candidate.sequence === event.sequence)) return state;

  let next: AgentActivityRunState = {
    ...state,
    seenEventIds: [...state.seenEventIds, event.eventId],
  };
  if (event.sequence > state.lastSequence + 1) {
    return {
      ...next,
      bufferedEvents: [...next.bufferedEvents, event].sort(
        (left, right) => left.sequence - right.sequence,
      ),
    };
  }

  next = applySequentialEvent(next, event);
  while (next.status === "running") {
    const buffered = next.bufferedEvents.find(
      (candidate) => candidate.sequence === next.lastSequence + 1,
    );
    if (!buffered) break;
    next = applySequentialEvent(
      {
        ...next,
        bufferedEvents: next.bufferedEvents.filter(
          (candidate) => candidate.eventId !== buffered.eventId,
        ),
      },
      buffered,
    );
  }
  return next;
}

export function selectAgentActivityStageItems(
  state: AgentActivityRunState,
): AgentActivityStageItem[] {
  const orderedKeys: string[] = [];
  const items = new Map<string, AgentActivityStageItem>();
  for (const event of state.events) {
    const attempt = event.attempt ?? 1;
    const key = `${event.stage}:${attempt}`;
    if (!items.has(key)) orderedKeys.push(key);
    items.set(key, {
      key,
      stage: event.stage,
      status: event.status,
      attempt,
      messageKey: event.messageKey,
      progress: event.progress,
      completedItems: event.completedItems,
      totalItems: event.totalItems,
      itemKind: event.itemKind,
      toolName: TOOL_NAME_SET.has(event.toolName ?? "")
        ? event.toolName as AgentActivityToolName
        : undefined,
    });
  }
  return orderedKeys
    .map((key) => items.get(key))
    .filter((item): item is AgentActivityStageItem => item !== undefined)
    .filter((item) => state.status === "running" || item.status !== "queued");
}

export function selectCurrentAgentActivityEvent(
  state: AgentActivityRunState,
): AgentActivityEvent | undefined {
  return [...state.events].reverse().find((event) => event.stage === state.currentStage);
}

export function agentActivityTitle(stage: AgentActivityStage): string {
  return AGENT_ACTIVITY_COPY[stage].title;
}

export function agentActivityPrimaryStatus(
  assistantName: string,
  stage: AgentActivityStage,
): string {
  const name = assistantName.trim() || "The agent";
  const action: Readonly<Record<AgentActivityStage, string>> = {
    understanding: "is getting your request into focus",
    planning: "is planning the best approach",
    reasoning: "is comparing the available evidence",
    searching_web: "is searching trusted web sources",
    crawling: "is exploring relevant websites",
    searching_knowledge: "is checking PERLab knowledge",
    reading_sources: "is reading the selected sources",
    analyzing: "is analyzing the evidence",
    using_tool: "is running the required tool",
    generating_image: "is creating your visual",
    generating_document: "is building your document",
    composing: "is bringing the findings together",
    validating: "is checking the result",
    success: "has completed the result",
    retrying: "is refocusing and trying again",
    error: "encountered a problem",
    cancelled: "has stopped the operation",
  };
  return `${name} ${action[stage]}`;
}

export function agentActivityMessages(stage: AgentActivityStage): readonly string[] {
  return AGENT_ACTIVITY_COPY[stage].messageKeys.map((key) => AGENT_ACTIVITY_MESSAGES[key]);
}

export function agentActivityMessage(
  event: AgentActivityEvent | undefined,
  messageIndex = 0,
): string {
  if (!event) return AGENT_ACTIVITY_MESSAGES["understanding.focus"];
  if (event.messageKey) return AGENT_ACTIVITY_MESSAGES[event.messageKey];
  const messages = agentActivityMessages(event.stage);
  return messages[Math.abs(messageIndex) % messages.length]
    ?? messages[0]
    ?? AGENT_ACTIVITY_MESSAGES["understanding.focus"];
}

export function safeAgentActivityToolLabel(toolName?: string): string | null {
  return toolName && TOOL_NAME_SET.has(toolName)
    ? AGENT_ACTIVITY_TOOL_LABELS[toolName as AgentActivityToolName]
    : null;
}

function fallbackStage(input: TruthfulActivityFallbackInput): {
  stage: AgentActivityStage;
  messageKey: AgentActivityMessageKey;
} {
  if (input.mode === "image") {
    return { stage: "generating_image", messageKey: "generating_image.shape" };
  }
  if (
    input.researchIntent === "market_benchmark"
    || input.researchIntent === "public_web_fallback"
  ) {
    return { stage: "searching_web", messageKey: "searching_web.check" };
  }
  if (input.knowledgeScope === "global") {
    return { stage: "searching_knowledge", messageKey: "searching_knowledge.perlab" };
  }
  return { stage: "reasoning", messageKey: "reasoning.request" };
}

/** Create the single coarse stage that the current blocking Zarvis API can truthfully support. */
export function createTruthfulFallbackActivity(
  input: TruthfulActivityFallbackInput,
): AgentActivityRunState {
  const state = createAgentActivityRunState(input.runId, input.startedAt);
  const fallback = fallbackStage(input);
  return reduceAgentActivityEvent(state, {
    schemaVersion: 1,
    runId: state.runId,
    eventId: `${state.runId}:fallback:1`,
    sequence: 1,
    attempt: 1,
    stage: fallback.stage,
    status: "active",
    messageKey: fallback.messageKey,
    startedAt: state.startedAt,
    timestamp: state.startedAt,
  });
}

function nonNegativeInteger(value: unknown): number {
  return optionalNonNegativeInteger(value) ?? 0;
}

function pluralized(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}

export function completionDetailsFromResponse(
  response: AgentActivityCompletionFacts,
): AgentActivityDetail[] {
  const details: AgentActivityDetail[] = [];
  if (response.image != null) {
    details.push({ id: "generated-visual", label: "Generated 1 visual" });
  }

  const citedCount = response.grounding?.cited_count === undefined
    ? response.citations?.length ?? 0
    : nonNegativeInteger(response.grounding.cited_count);
  if (citedCount > 0) {
    details.push({
      id: "cited-sources",
      label: `Cited ${citedCount} ${pluralized(citedCount, "source")}`,
    });
  }

  const retrievedCount = nonNegativeInteger(response.grounding?.retrieved_count);
  if (retrievedCount > citedCount) {
    details.push({
      id: "retrieved-evidence",
      label: `Retrieved ${retrievedCount} evidence ${pluralized(retrievedCount, "item")}`,
    });
  }

  const webQueries = new Set(
    (response.grounding?.bing_query_urls ?? []).filter((url) => typeof url === "string" && url.length > 0),
  ).size;
  if (webQueries > 0) {
    details.push({
      id: "public-web-queries",
      label: `Checked ${webQueries} public web ${pluralized(webQueries, "query", "queries")}`,
    });
  }

  const globalCount = nonNegativeInteger(response.grounding?.global_retrieved_count);
  if (globalCount > 0) {
    details.push({
      id: "global-knowledge",
      label: `Used ${globalCount} authorized PERLab knowledge ${pluralized(globalCount, "source")}`,
    });
  }
  return details;
}

function currentEventCanFinish(state: AgentActivityRunState): AgentActivityEvent | undefined {
  const current = selectCurrentAgentActivityEvent(state);
  return current && !TERMINAL_STAGE_STATUSES.has(current.status) ? current : undefined;
}

function applyAuthoritativeTerminalEvents(
  state: AgentActivityRunState,
  events: readonly Omit<AgentActivityEvent, "sequence" | "eventId" | "runId" | "schemaVersion">[],
): AgentActivityRunState {
  let next: AgentActivityRunState = { ...state, bufferedEvents: [] };
  for (const event of events) {
    const sequence = next.lastSequence + 1;
    next = reduceAgentActivityEvent(next, {
      ...event,
      schemaVersion: 1,
      runId: next.runId,
      eventId: `${next.runId}:terminal:${sequence}:${event.stage}`,
      sequence,
    });
  }
  return next;
}

/** Mark the current coarse stage complete, then add an authoritative success event. */
export function completeAgentActivityRun(
  state: AgentActivityRunState,
  response: AgentActivityCompletionFacts,
  timestamp?: string,
): AgentActivityRunState {
  if (state.status === "completed") {
    return {
      ...state,
      // A progress success can precede the answer; include that final wait once.
      endedAt: state.responseStatus === undefined && response.response_status !== undefined
        ? nowTimestamp(timestamp)
        : state.endedAt,
      responseStatus: response.response_status ?? state.responseStatus,
      details: completionDetailsFromResponse(response),
      retryable: false,
    };
  }
  if (state.status !== "running") return state;
  const endedAt = nowTimestamp(timestamp);
  const current = currentEventCanFinish(state);
  const messageKey: AgentActivityMessageKey = response.response_status === "clarification"
    ? "success.clarification"
    : response.response_status === "insufficient_evidence"
      ? "success.evidence_gap"
      : "success.ready";
  const terminalEvents: Omit<
    AgentActivityEvent,
    "sequence" | "eventId" | "runId" | "schemaVersion"
  >[] = [];
  if (current) {
    terminalEvents.push({
      attempt: current.attempt,
      stage: current.stage,
      status: "completed",
      messageKey: current.messageKey,
      completedItems: current.completedItems,
      totalItems: current.totalItems,
      itemKind: current.itemKind,
      toolName: current.toolName,
      startedAt: current.startedAt,
      timestamp: endedAt,
    });
  }
  terminalEvents.push({
    attempt: 1,
    stage: "success",
    status: "completed",
    messageKey,
    timestamp: endedAt,
  });
  const completed = applyAuthoritativeTerminalEvents(state, terminalEvents);
  return {
    ...completed,
    responseStatus: response.response_status,
    details: completionDetailsFromResponse(response),
    retryable: false,
  };
}

const FAILURE_MESSAGE_KEYS: Readonly<Record<AgentActivityFailureKind, AgentActivityMessageKey>> = {
  unknown: "error.failed",
  temporary: "error.temporary",
  validation: "error.validation",
  access: "error.access",
  rate_limited: "error.rate_limited",
};

/** Finish a run with bounded safe copy. Raw Error messages are intentionally not accepted. */
export function failAgentActivityRun(
  state: AgentActivityRunState,
  failure: AgentActivityFailure = {},
): AgentActivityRunState {
  if (state.status !== "running" && !(
    state.status === "completed" && state.responseStatus === undefined
  )) return state;
  const endedAt = nowTimestamp(failure.timestamp);
  const current = currentEventCanFinish(state);
  const terminalEvents: Omit<
    AgentActivityEvent,
    "sequence" | "eventId" | "runId" | "schemaVersion"
  >[] = [];
  if (current) {
    terminalEvents.push({
      attempt: current.attempt,
      stage: current.stage,
      status: "failed",
      messageKey: current.messageKey,
      progress: current.progress,
      completedItems: current.completedItems,
      totalItems: current.totalItems,
      itemKind: current.itemKind,
      toolName: current.toolName,
      startedAt: current.startedAt,
      timestamp: endedAt,
    });
  }
  terminalEvents.push({
    attempt: 1,
    stage: "error",
    status: "failed",
    messageKey: FAILURE_MESSAGE_KEYS[failure.kind ?? "unknown"],
    timestamp: endedAt,
  });
  const failed = applyAuthoritativeTerminalEvents({ ...state, status: "running" }, terminalEvents);
  return { ...failed, retryable: failure.retryable === true };
}

export function cancelAgentActivityRun(
  state: AgentActivityRunState,
  timestamp?: string,
): AgentActivityRunState {
  if (state.status !== "running" && !(
    state.status === "completed" && state.responseStatus === undefined
  )) return state;
  const endedAt = nowTimestamp(timestamp);
  const current = currentEventCanFinish(state);
  const terminalEvents: Omit<
    AgentActivityEvent,
    "sequence" | "eventId" | "runId" | "schemaVersion"
  >[] = [];
  if (current) {
    terminalEvents.push({
      attempt: current.attempt,
      stage: current.stage,
      status: "cancelled",
      messageKey: current.messageKey,
      timestamp: endedAt,
    });
  }
  terminalEvents.push({
    attempt: 1,
    stage: "cancelled",
    status: "cancelled",
    messageKey: "cancelled.stopped",
    timestamp: endedAt,
  });
  return applyAuthoritativeTerminalEvents({ ...state, status: "running" }, terminalEvents);
}

export function formatAgentActivityElapsed(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  if (wholeSeconds < 60) return `${wholeSeconds}s`;
  const minutes = Math.floor(wholeSeconds / 60);
  const remainingSeconds = wholeSeconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
